import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { loadTopEarningClips, topEarningSpreadsheetRows, type TopEarningProgress } from '@/lib/campaigns/loadTopEarningClips';

export function TopEarningClipsExportButton({ campaignId, currency }: { campaignId: string; currency: string }) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const active = useRef<AbortController | null>(null);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState<TopEarningProgress | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setExporting(false);
    setProgress(null);
    setFailed(false);
    return () => { active.current?.abort(); active.current = null; };
  }, [campaignId]);

  const download = async () => {
    if (!campaignId || active.current) return;
    const controller = new AbortController();
    active.current = controller;
    const isCurrent = () => active.current === controller && !controller.signal.aborted;
    setExporting(true);
    setProgress(null);
    setFailed(false);
    try {
      const clips = await loadTopEarningClips(client, campaignId, controller.signal, (next) => {
        if (isCurrent()) setProgress(next);
      });
      if (!isCurrent()) return;
      const XLSX = await import('xlsx');
      if (!isCurrent()) return;
      const headers = ['topEarning.rank', 'table.platform', 'table.url', 'table.submitter', 'table.views', 'table.earned', 'topEarning.currency']
        .map((key) => t(`admin.campaigns.submissions.${key}`));
      const sheet = XLSX.utils.aoa_to_sheet([headers, ...topEarningSpreadsheetRows(clips, currency)]);
      sheet['!cols'] = [{ wch: 10 }, { wch: 14 }, { wch: 60 }, { wch: 36 }, { wch: 22 }, { wch: 16 }, { wch: 10 }];
      for (let index = 0; index < clips.length; index += 1) sheet[`F${index + 2}`].z = '#,##0.00';
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Ranking');
      const safeId = campaignId.replace(/[^a-zA-Z0-9_-]/g, '');
      XLSX.writeFile(workbook, `ranking-clipes-${safeId}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      if (isCurrent()) setFailed(true);
    } finally {
      if (isCurrent()) {
        active.current = null;
        setExporting(false);
        setProgress(null);
      }
    }
  };

  const label = exporting
    ? progress ? t('admin.campaigns.submissions.topEarning.exportProgress', progress) : t('admin.campaigns.submissions.topEarning.exporting')
    : t('admin.campaigns.submissions.topEarning.exportSpreadsheet');
  return <>
    <Button type="button" variant="outline" size="sm" onClick={download} disabled={!campaignId || exporting} aria-busy={exporting}>
      {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <Download className="mr-2 h-4 w-4" aria-hidden />}
      {label}
    </Button>
    <span role="status" className="sr-only" aria-live="polite">{exporting ? label : ''}</span>
    <Dialog open={failed} onOpenChange={setFailed}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.campaigns.submissions.topEarning.exportErrorTitle')}</DialogTitle>
          <DialogDescription>{t('admin.campaigns.submissions.topEarning.exportFailed')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setFailed(false)}>{t('common.close')}</Button>
          <Button onClick={download}>{t('common.retry')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
